import { useMemo, useState } from "react";
import { relievingPressureKpaAbs, type ValveType } from "../calc/gas";
import {
  sizeSubcooledLiquid,
  type SubcooledSizingInput,
} from "../calc/subcooledLiquid";
import { selectOrificeWithParallel } from "../calc/orifice";
import { Field, Item, VALVE_LABELS, num } from "./formBits";

type Certification = "certified" | "non-certified";

interface FormState {
  certification: Certification;
  flow: string;
  rhoL1: string;
  rho9: string;
  saturationPressure: string;
  setPressure: string;
  overpressure: string;
  backpressure: string;
  valveType: ValveType;
  kb: string;
  kc: string;
  kv: string;
  kp: string;
  useApprox: boolean;
}

/** 초기값은 API 520 Part I Annex C.2.3.2 예제(프로판) 조건이다. 결과는 F 오리피스. */
const INITIAL: FormState = {
  certification: "certified",
  flow: "378.5",
  rhoL1: "511.3",
  rho9: "262.7",
  saturationPressure: "741.875",
  setPressure: "1792.6", // 260 psig
  overpressure: "10",
  backpressure: "170.3", // 10 psig = 24.7 psia
  valveType: "conventional",
  kb: "1.0",
  kc: "1.0",
  kv: "1.0",
  kp: "",
  useApprox: false,
};

export function SubcooledSizingPanel() {
  const [f, setF] = useState<FormState>(INITIAL);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setF((prev) => ({ ...prev, [key]: value }));

  const p1Abs = useMemo(
    () => relievingPressureKpaAbs(num(f.setPressure), num(f.overpressure)),
    [f.setPressure, f.overpressure],
  );
  const p1Gauge = useMemo(
    () => num(f.setPressure) * (1 + num(f.overpressure) / 100),
    [f.setPressure, f.overpressure],
  );

  const sizing = useMemo(() => {
    const kb = f.kb.trim() === "" ? undefined : num(f.kb);
    const kp = f.kp.trim() === "" ? undefined : num(f.kp);
    const common = {
      volumetricFlowLpm: num(f.flow),
      inletLiquidDensityKgM3: num(f.rhoL1),
      flashDensityKgM3: num(f.rho9),
      saturationPressureKpaAbs: num(f.saturationPressure),
      relievingPressureKpaAbs: p1Abs,
      backpressureKpaAbs: num(f.backpressure),
      valveType: f.valveType,
      combinationCorrection: num(f.kc),
      viscosityCorrection: num(f.kv),
      useApproximateCriticalRatio: f.useApprox,
      ...(kb === undefined ? {} : { bellowsBackpressureCorrection: kb }),
    };

    const input: SubcooledSizingInput =
      f.certification === "certified"
        ? { ...common, certification: "certified" }
        : {
            ...common,
            certification: "non-certified",
            setPressureKpaGauge: num(f.setPressure),
            relievingPressureKpaGauge: p1Gauge,
            // 게이지 배압 = 절대 배압 − 대기압
            backpressureKpaGauge: num(f.backpressure) - 101.325,
            ...(kp === undefined ? {} : { overpressureCorrection: kp }),
          };
    return sizeSubcooledLiquid(input);
  }, [f, p1Abs, p1Gauge]);

  const selection = useMemo(
    () => (sizing.ok ? selectOrificeWithParallel(sizing.requiredAreaMm2) : null),
    [sizing],
  );

  const isBellows = f.valveType === "balanced-bellows";
  const isNonCertified = f.certification === "non-certified";
  const flashPressure = num(f.saturationPressure) * 0.9;

  return (
    <section className="card">
      <h2>2상 유동 — 입구 과냉 액체 (Omega 법)</h2>
      <p className="hint">
        API 520 Part I 10판 Annex C.2.3. 입구가 과냉(또는 포화) 액체이고 밸브를
        지나며 플래시하는 경우입니다. <strong>밀도 두 점</strong>이 필요합니다 —
        입구 액체 밀도 ρ<sub>l1</sub> 과, 포화압의 90 %({flashPressure.toFixed(1)} kPa)에서
        플래시한 밀도 ρ₉ 입니다. 초기값은 표준서 예제(프로판) 조건입니다.
      </p>

      <div className="grid-form">
        <Field label="용량인증" unit="" help="certification">
          <select
            value={f.certification}
            onChange={(e) => set("certification")(e.target.value as Certification)}
          >
            <option value="certified">인증 밸브 (C.2.3.1, Kd 0.65)</option>
            <option value="non-certified">비인증 밸브 (C.2.3.3, Kd 0.62)</option>
          </select>
        </Field>
        <Field label="체적 유량 Q" unit="L/min" help="flowVolume">
          <input value={f.flow} onChange={(e) => set("flow")(e.target.value)} />
        </Field>
        <Field label="입구 액체밀도 ρl1" unit="kg/m³" help="inletLiquidDensity">
          <input value={f.rhoL1} onChange={(e) => set("rhoL1")(e.target.value)} />
        </Field>
        <Field label="플래시 밀도 ρ₉" unit="kg/m³ (0.9·Ps)" help="flashDensity">
          <input value={f.rho9} onChange={(e) => set("rho9")(e.target.value)} />
        </Field>
        <Field label="포화압 Ps" unit="kPa abs" help="saturationPressure">
          <input
            value={f.saturationPressure}
            onChange={(e) => set("saturationPressure")(e.target.value)}
          />
        </Field>
        <Field label="설정압" unit="kPag" help="setPressure">
          <input
            value={f.setPressure}
            onChange={(e) => set("setPressure")(e.target.value)}
          />
        </Field>
        <Field label="허용 과압" unit="%" help="overpressure">
          <input
            value={f.overpressure}
            onChange={(e) => set("overpressure")(e.target.value)}
          />
        </Field>
        <Field label="총 배압 P2" unit="kPa abs" help="backpressureAbs">
          <input
            value={f.backpressure}
            onChange={(e) => set("backpressure")(e.target.value)}
          />
        </Field>
        <Field label="밸브 형식" unit="" help="valveType">
          <select
            value={f.valveType}
            onChange={(e) => set("valveType")(e.target.value as ValveType)}
          >
            {(Object.keys(VALVE_LABELS) as ValveType[]).map((t) => (
              <option key={t} value={t}>
                {VALVE_LABELS[t]}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="배압 보정 Kb"
          unit={isBellows ? "Figure C.3 / 제작사" : "해당 없음"}
          help="kbLiquidBellows"
        >
          <input
            value={f.kb}
            disabled={!isBellows}
            onChange={(e) => set("kb")(e.target.value)}
          />
        </Field>
        <Field label="점도 보정 Kv" unit="100 cP 이하면 1.0" help="kvTwoPhase">
          <input value={f.kv} onChange={(e) => set("kv")(e.target.value)} />
        </Field>
        <Field label="파열판 조합 Kc" unit="없으면 1.0" help="kc">
          <input value={f.kc} onChange={(e) => set("kc")(e.target.value)} />
        </Field>
        <Field
          label="과압 보정 Kp"
          unit={isNonCertified ? "10 % 과압이면 0.6" : "해당 없음"}
          help="kpNonCertified"
        >
          <input
            value={f.kp}
            disabled={!isNonCertified}
            placeholder={isNonCertified ? "25 % 과압이면 1.0" : "해당 없음"}
            onChange={(e) => set("kp")(e.target.value)}
          />
        </Field>
        <Field label="임계 압력비 산출" unit="저과냉 영역에서만" help="criticalRatioMethod">
          <select
            value={f.useApprox ? "approx" : "exact"}
            onChange={(e) => set("useApprox")(e.target.value === "approx")}
          >
            <option value="exact">정의식 (C.37) 수치해</option>
            <option value="approx">근사식 (C.38)</option>
          </select>
        </Field>
      </div>

      {!sizing.ok ? (
        <div className="result result--error" role="alert">
          {sizing.message}
        </div>
      ) : (
        <div className="result result--ok">
          <div className="result__headline">
            <span className="result__label">소요 면적</span>
            <strong className="result__value">
              {sizing.requiredAreaMm2.toLocaleString("ko-KR", {
                maximumFractionDigits: 1,
              })}
            </strong>
            <span className="result__label">mm²</span>
            <span className="badge">
              {sizing.subcoolingRegion === "high" ? "고과냉" : "저과냉"}
            </span>
            <span className="badge">
              {sizing.flowRegime === "critical" ? "임계유동" : "아임계유동"}
            </span>
          </div>

          {selection?.ok && (
            <div className="result__headline" style={{ marginTop: 10 }}>
              <span className="result__label">선정 오리피스</span>
              <strong className="result__value">
                {selection.orifice.designation}
              </strong>
              {selection.valveCount > 1 && (
                <span className="badge">{selection.valveCount}개 병렬</span>
              )}
              <span className="muted">
                여유율 {selection.marginPct.toFixed(1)} %
              </span>
            </div>
          )}
          {selection && !selection.ok && (
            <p className="muted" style={{ marginTop: 10 }}>
              {selection.message}
            </p>
          )}

          <h3>중간값 (계산서 기재용)</h3>
          <dl className="result__grid">
            <Item label="적용 식" value={sizing.equation} />
            <Item
              label="오메가 ωs (식 C.30)"
              value={sizing.intermediates.omegaS.toFixed(4)}
            />
            <Item
              label="전이비 ηst (식 C.32)"
              value={sizing.intermediates.transitionRatio.toFixed(4)}
            />
            <Item
              label="포화압력비 ηs = Ps/P1"
              value={sizing.intermediates.saturationRatio.toFixed(4)}
            />
            <Item
              label={`임계압력비 ηc (${sizing.intermediates.criticalRatioSource.replace("API 520 Part I ", "식 ")})`}
              value={sizing.intermediates.criticalPressureRatio.toFixed(4)}
            />
            <Item
              label="배압비 ηa = P2/P1"
              value={sizing.intermediates.backpressureRatio.toFixed(4)}
            />
            <Item
              label={`질량유속 G (${sizing.intermediates.massFluxEquation.replace("API 520 Part I ", "식 ")})`}
              value={`${sizing.intermediates.massFlux.toLocaleString("ko-KR", { maximumFractionDigits: 0 })} kg/s·m²`}
            />
            <Item
              label="입구 압력 P1"
              value={`${p1Abs.toLocaleString("ko-KR", { maximumFractionDigits: 1 })} kPa abs`}
            />
            <Item label="Kd" value={sizing.intermediates.kd.toFixed(3)} />
            <Item label="Kb" value={sizing.intermediates.kb.toFixed(3)} />
            <Item label="Kc" value={sizing.intermediates.kc.toFixed(3)} />
            <Item label="Kv" value={sizing.intermediates.kv.toFixed(3)} />
            {sizing.intermediates.kp !== null && (
              <Item label="Kp" value={sizing.intermediates.kp.toFixed(3)} />
            )}
          </dl>

          {sizing.warnings.length > 0 && (
            <ul className="warnings">
              {sizing.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
